import React from "react";

import { colorsSets } from "@similarweb/styles";
import { CircleSwitcherItem, Switcher, PixelPlaceholderLoader } from "@similarweb/ui-components";

import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import Chart from "pro-features/components/Chart/src/Chart";

import { isLowConfidence } from "pro-features/components/Chart/src/data/confidenceProcessor";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { PdfExportService } from "app/services/PdfExportService";
import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import { i18nFilter } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { EngagementVerticals } from "app/pages/segments/mmx/EngagementVerticals";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import { FlexColumn, RightFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { EDisplayType, displayTypeItems } from "./displayTypeItems";
import { SegmentsMmxChannelsMapping } from "./SegmentsMarketingChannelsConfig";
import {
    barChartGraphDashed,
    segmentsMarketingChannelsBarChartConfig,
} from "./SegmentsSingleMarketingChannelsBarChartConfig";
import {
    BarChartOverviewContainer,
    BoxContainer,
    MainValueContainer,
    NoDataGraphContainer,
    SectionContainer,
    SectionLine,
    SegmentsBarChartStyles,
    StyledHeaderTitle,
    SwitcherContainer,
    TitleContainer,
} from "./styledComponents";

const MIN_VALUE_TO_SHOW_DATA = 5000;

export const SegmentsMmxSingleOverview = (props) => {
    const { i18n } = React.useMemo(
        () => ({
            i18n: i18nFilter(),
        }),
        [],
    );

    const { params, data, isLoading } = props;
    const { id, duration, comparedDuration, country, webSource } = params;

    const durationObject = React.useMemo(
        () => DurationService.getDurationData(duration, comparedDuration),
        [duration, comparedDuration],
    );

    const subtitleFilters = React.useMemo(
        () => [
            {
                filter: "date",
                value: {
                    from: durationObject.raw.from.valueOf(),
                    to: durationObject.raw.to.valueOf(),
                    useRangeDisplay:
                        duration !== "28d" &&
                        durationObject.raw.from.format("YYYY-MM") !== durationObject.raw.to.format("YYYY-MM"),
                },
            },
            {
                filter: "country",
                countryCode: country,
                value: CountryService.getCountryById(country)?.text,
            },
            {
                filter: "webSource",
                value: webSource,
            },
        ],
        [durationObject, country, webSource, duration],
    );

    const [selectedDisplayTypeIndex, setSelectedDisplayTypeIndex] = React.useState(0);
    const selectedDisplayTypeItem = React.useMemo(
        () => displayTypeItems[selectedDisplayTypeIndex],
        [selectedDisplayTypeIndex],
    );

    const barChartConfig = React.useMemo(
        () =>
            segmentsMarketingChannelsBarChartConfig({
                valueType: selectedDisplayTypeItem.name,
                categories: SegmentsMmxChannelsMapping,
            }),
        [selectedDisplayTypeItem],
    );

    const siteData = data?.Data?.[id]?.[EngagementVerticals.Visits.dataKey];
    const { totalTrafficValue, barChartData } = React.useMemo(() => {
        const totalTrafficValue: any = siteData
            ? Object.values(siteData)?.reduce((acc, { Value }) => acc + Value, 0)
            : null;
        const colorsSet = colorsSets.c.toArray();
        const barChartData = barChartGraphDashed(
            [
                {
                    data: SegmentsMmxChannelsMapping.map(({ dataKey }, i) => {
                        const point = siteData?.[dataKey];
                        const val = point
                            ? selectedDisplayTypeItem.name === "numeric"
                                ? point.Value
                                : ((totalTrafficValue && point.Value / totalTrafficValue) ?? null)
                            : null;
                        const isLowConfidenceValue = isLowConfidence(point?.Confidence);
                        return {
                            y: val,
                            isLowConfidence: isLowConfidenceValue,
                            color: colorsSet[i % colorsSet.length],
                        };
                    }),
                },
            ],
            (point) => point.isLowConfidence,
        );
        return { totalTrafficValue, barChartData };
    }, [siteData, selectedDisplayTypeItem]);

    const barChartRef = React.useRef<HTMLDivElement>(null);
    const downloadPNG = React.useCallback((): Promise<void> => {
        allTrackers.dispatchTrackEvent(
            "segments.mmx.single.bar.graph.download.png",
            "submit-ok",
            "segments.mmx.single.bar.graph.download.png",
        );
        const offSetX = 0;
        const offSetY = 50;
        const styleHTML = PdfExportService.getStylesheetsForPng();
        return PdfExportService.downloadHtmlPngFedService(
            styleHTML + barChartRef.current.outerHTML,
            "SegmentsMmxChartOverview",
            barChartRef.current.offsetWidth + offSetX,
            barChartRef.current.offsetHeight + offSetY,
        ).then();
    }, []);

    const toggleSelectedDisplay = (index) => {
        setSelectedDisplayTypeIndex(index);
        allTrackers.dispatchTrackEvent("Measure Button", "click", "Channel overview", {
            Value: index === EDisplayType.PERCENTAGE ? "percentage" : "number",
        });
    };

    return (
        <BoxContainer ref={barChartRef}>
            <TitleContainer>
                <FlexColumn>
                    <StyledHeaderTitle>
                        <BoxTitle tooltip={i18n("segments.analysis.mmx.single.overview.title.tooltip")}>
                            {i18n("segments.analysis.mmx.single.overview.title")}
                        </BoxTitle>
                    </StyledHeaderTitle>
                    <StyledBoxSubtitle>
                        <BoxSubtitle filters={subtitleFilters} />
                    </StyledBoxSubtitle>
                </FlexColumn>
                <RightFlexRow>
                    <DownloadButtonMenu
                        PNG={true}
                        handleDownloadFunction={downloadPNG}
                    />
                </RightFlexRow>
            </TitleContainer>
            <SectionContainer>
                <SectionLine>
                    <MainValueContainer>
                        <div className="mainValue">
                            {isLoading ? (
                                <PixelPlaceholderLoader
                                    width="60px"
                                    height="20px"
                                />
                            ) : !data || totalTrafficValue === 0 ? (
                                "N/A"
                            ) : (
                                abbrNumberFilter()(totalTrafficValue)
                            )}
                        </div>
                        <div className="mainValueDesc">
                            {i18n("segments.analysis.mmx.single.overview.total.traffic")}
                        </div>
                    </MainValueContainer>
                    <SwitcherContainer>
                        <Switcher
                            customClass="CircleSwitcher"
                            className="chartSwitcher"
                            selectedIndex={selectedDisplayTypeIndex}
                            onItemClick={toggleSelectedDisplay}
                        >
                            {displayTypeItems.map((displayTypeItem) => (
                                <CircleSwitcherItem key={displayTypeItem.name}>
                                    {displayTypeItem.display}
                                </CircleSwitcherItem>
                            ))}
                        </Switcher>
                    </SwitcherContainer>
                </SectionLine>
            </SectionContainer>
            <SectionContainer>
                <SegmentsBarChartStyles />
                <BarChartOverviewContainer>
                    {isLoading ? (
                        <PixelPlaceholderLoader
                            width="100%"
                            height="260px"
                        />
                    ) : !data || totalTrafficValue < MIN_VALUE_TO_SHOW_DATA ? (
                        <NoDataGraphContainer>
                            <TableNoData
                                icon="no-data"
                                messageTitle={i18nFilter()("global.nodata.notavilable")}
                            />
                        </NoDataGraphContainer>
                    ) : (
                        <Chart
                            type="bar"
                            data={barChartData}
                            config={barChartConfig}
                        />
                    )}
                </BarChartOverviewContainer>
            </SectionContainer>
        </BoxContainer>
    );
};
